import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { Actions, ofType } from '@ngrx/effects';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { forkJoin, of, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AddUserRoleSuccessAction, AddUserRoleAction, RemoveUserRoleAction } from 'src/app/user/store/user.actions';
import { accessSelector } from 'src/app/global/store/global.selectors';
import { AccessControl, Role, UserRoleResponce } from 'src/app/global/services/access-control/access-control.model';
import { DeleteAccessAction, DeleteAccessErrorAction, DeleteAccessSuccessAction, GetAccessAction } from 'src/app/global/store/global.actions';
import { Router } from '@angular/router';
import { AccessControlService } from 'src/app/global/services/access-control/access-control.abstract.service';
import { AccessControlRoleDialogComponent } from '../access-control-role-dialog/access-control-role-dialog.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
    selector: 'app-user-role-modal',
    templateUrl: './transfer-dialog.component.html',
    styleUrls: ['./transfer-dialog.component.scss'],
})
export class TransferDialogComponent implements OnInit {
    public loading = false;
    public destroy$ = new Subject<void>();

    // Role management
    availableRoles: AccessControl[] = [];
    selectedRole: AccessControl | null = null;

    // User management
    selectedUsers: any[] = [];
    assignedUsers: any[] = [];
    public showNoUsersFound = false;
    public assignableRoles: Role[] = [];
    roleSelected: boolean = false;

    @Output() onSubmit: EventEmitter<any> = new EventEmitter<any>();

    constructor(
        private router: Router,
        private store: Store<State>,
        private actions$: Actions,
        private dialog: MatDialog,
        private accessService: AccessControlService,
        private messageService: MessageService,
        private translate: TranslateService,
        public dialogRef: MatDialogRef<AccessControlRoleDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AccessControl,
    ) {}

    ngOnInit(): void {
        this.loadRoles();
        // If data contains users, set them as selected users for transfer
        if (this.data?.users) {
            this.selectedUsers = this.data?.users;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadRoles(): void {
        // Load available roles from store
        this.store
            .select(accessSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((roles) => {
                this.availableRoles = roles.filter((role) => role._id !== this.data._id);
                this.availableRoles.forEach((role) => {
                    this.assignableRoles.push({
                        name: role.title,
                        id: role?._id,
                        userId: '',
                        selected: false,
                    });
                });
                if (roles.length === 0) {
                    // If no roles in store, dispatch action to load them
                    this.store.dispatch(GetAccessAction());
                }
            });
    }

    onRoleSelected(role: AccessControl): void {
        this.selectedRole = role;
        this.loadUsersForRole(role._id!);
    }

    private loadUsersForRole(roleId: string): void {
        this.loading = true;
        this.accessService
            .getRoleUsers(roleId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: UserRoleResponce) => {
                    this.assignedUsers = response.data || [];
                    this.loading = false;
                },
                error: (error) => {
                    console.error('Error loading users for role:', error);
                    this.assignedUsers = [];
                    this.loading = false;
                },
            });
    }

    onUsersSelected(users: any[]): void {
        this.selectedUsers = users;
    }

    onNoUsersFound(noUsersFound: boolean): void {
        this.showNoUsersFound = noUsersFound;
    }

    roleIsSelected(): boolean {
        this.roleSelected = false;
        this.assignableRoles.forEach((role) => {
            if (role.selected == true) {
                this.roleSelected = true;
            }
        });
        return this.roleSelected;
    }

    // Add this method to handle role selection changes from the child
    onRoleSelectionChanged(selectedRoles: Role[]): void {
        // Update assignableRoles' selected state based on the selectedRoles array
        this.assignableRoles.forEach((role) => {
            role.selected = selectedRoles.some((selected) => selected.id === role.id);
        });
    }

    transferUsers(): void {
        if (this.roleIsSelected()) {
            this.loading = true;
            // Dispatch AddUserRoleAction for each selected user
            this.assignableRoles.forEach((role) => {
                if (role.selected == true) {
                    this.selectedUsers.forEach((user) => {
                        this.store.dispatch(AddUserRoleAction({ roleId: role.id, userid: user._id }));
                    });
                }
            });

            // Listen for success actions to update the UI
            this.actions$
                .pipe(
                    ofType(AddUserRoleSuccessAction),
                    take(this.selectedUsers.length), // Wait for all users to be processed
                    takeUntil(this.destroy$),
                    // After all successful assignments, refresh the role users
                    switchMap(() => {
                        const selectedRoles = this.assignableRoles.filter((role) => role.selected === true);

                        if (selectedRoles.length === 0) {
                            return of([]); // Return empty observable if no selected roles
                        }

                        // Create array of observables for all selected roles
                        const roleUserRequests = selectedRoles.map((role) => this.accessService.getRoleUsers(role.id!));

                        // Execute all requests in parallel and wait for all to complete
                        return forkJoin(roleUserRequests);
                    }),
                )
                .subscribe({
                    next: (userRoleResponse: UserRoleResponce[]) => {
                        // IMPORTANT: Refresh the access control data to update the table
                        this.store.dispatch(GetAccessAction());

                        this.selectedUsers.forEach((user) => {
                            this.store.dispatch(RemoveUserRoleAction({ roleId: this.data._id, userid: user._id }));
                        });

                        // Show success message
                        this.messageService.success(this.translate.instant('access_control.dialog.success'));
                        this.openDelete();
                        // Reset form and close dialog
                        this.selectedUsers = [];
                        this.loading = false;
                        this.dialogRef.close(true);
                    },
                    error: (error) => {
                        console.error('Error refreshing role users after assignment:', error);
                        this.loading = false;
                    },
                });
        }
    }

    close(): void {
        this.dialogRef.close();
    }

    openDelete(): void {
        const access: AccessControl = this.data;
        const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
            autoFocus: false,
            panelClass: 'bciti-confirmation-dialog',
            data: {
                title: this.translate.instant('access_control.dialog.delete_title'),
                body: this.translate.instant('access_control.dialog.delete_empty', { name: this.data.title }),
                buttons: {
                    cancel: {
                        text: this.translate.instant('access_control.dialog.cancel'),
                        class: 'bciti-button bciti-button--black-inversed',
                    },
                    confirm: {
                        text: this.translate.instant('access_control.dialog.delete'),
                        class: 'bciti-button bciti-button--black',
                    },
                },
            },
        });

        // handle user confirm
        confirmDialogRef.componentInstance.onSubmit.subscribe((confirm) => {
            if (confirm) {
                confirmDialogRef.componentInstance.loading = true;
                this.store.dispatch(DeleteAccessAction({ access }));

                this.actions$
                    .pipe(
                        ofType(DeleteAccessSuccessAction, DeleteAccessErrorAction),
                        take(1),
                        tap(() => (confirmDialogRef.componentInstance.loading = false)),
                        ofType(DeleteAccessSuccessAction),
                        tap(() => confirmDialogRef.close()),
                    )
                    .subscribe();
            } else {
                confirmDialogRef.close();
            }
        });
    }

    cancel(): void {
        this.dialogRef.close();
    }

    getRoleDisplayText(role: AccessControl): string {
        return typeof role.title === 'string' ? role.title : role.title[this.translate.currentLang] || role.title.en || role.title.fr || 'Unknown Role';
    }

    getRoleDescriptionText(role: AccessControl): string {
        return typeof role.description === 'string' ? role.description : role.description[this.translate.currentLang] || role.description.en || role.description.fr || 'Unknown Role';
    }

    getUserAvatarUrl(user: any): string {
        return user?.picture || `https://www.pphfoundation.ca/wp-content/uploads/2018/05/default-avatar.png`;
    }

    getUserDisplayName(user: any): string {
        return user?.name || user?.firstName + ' ' + user?.lastName || user?.email || 'Unknown User';
    }

    trackByUserId(index: number, user: any): any {
        return user.id || user._id || index;
    }

    removeSelectedUser(userToRemove: any): void {
        this.selectedUsers = this.selectedUsers.filter((user) => (user.id || user._id) !== (userToRemove.id || userToRemove._id));
    }
}
